import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { Box, Grid, makeStyles, Divider } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router';

import { GeoInfo } from 'src/scenes/Project/scenes/Analytics/components';
import { buildLeafletLayer, buildLeafletLayerWithHeaders, buildLegendForIndex, getColorMapLegend }
  from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { AutocompleteWrapper, MapWrapper, FilterYearMonthComparison, FilterYearComparison, Chip } from 'src/components';
import { buildTiTilerUrl, getMonthString, formatDashedString, stringToCamelCase } from 'src/utils/util';
import {
  LoadingBox,
  ProcessBox,
  GeoSpatialYearComparison,
  GeospatialChart,
  LandcoverStats,
  NvpStats,
} from 'src/scenes/Project/scenes/Dashboard/components';
import { useProjectApi } from 'src/services';
import { AnalyticCard } from 'src/scenes/Project/components';
import analyticPalettes from 'src/utils/analyticPalettes';
import {
  getMinMaxValues,
  groupAssetsByType,
  isYearlyMode,
  selectDefaultAssets,
  buildAssetDate,
  findEiiDetailOptionByGeoInfoTypeId,
  GEOSPATIAL_OPTIONS_BY_EII_DETAIL,
  legendTitle,
} from 'src/utils/geospatialHelpers';


const heightVH = n => document.documentElement.clientHeight * (n / 100);

const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 18,
  doubleClickZoom: false,
  attributionControl: false,
};

const highZoomGeoInfoTypes = [ 'ndvi-drone', 'rgb-drone' ];
const highMapMaxZoom = 20;
const defaultLeafletMaxZoom = 18;

const useStyles = makeStyles(theme => ({
  map: {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  comparisonYearGroup: {
    border: `1px solid ${theme.palette.common.gray}`,
    borderRadius: theme.shape.borderRadius,
  },
  autocomplete: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
}));

const interestAreaMapHeight = 50;

const formatAssetName = (asset, isYearly) => {
  if (!asset) {
    return '';
  }
  if (isYearly) {
    return asset.year.toString();
  } else {
    const monthName = getMonthString(parseInt(asset.month));
    return `${monthName} ${asset.year}`;
  }
};

const GeospatialAnalytic = props => {

  const {
    title,
    interestArea,
    getLeafletOptions,
    analytic,
    id = 'spectral-indices-map',
    eiiDetailConfig,
    geospatialTypesOptions = [],
    fetchStatus,
    isUnique = false,
  } = props;

  const classes = useStyles();

  const isEiiDetail = Boolean(eiiDetailConfig);

  const { options: eiiDetailOptions = [], selected: selectedEiiDetail, onChange: onEiiDetailChange } = eiiDetailConfig || {};

  const projectApi = useProjectApi();
  const match = useRouteMatch();
  const componentId = match.url.split('/').filter(Boolean).pop();

  const geospatialStats = analytic?.stats ?? { data: undefined, hasData: false, inProgress: false };
  const [ geospatialSelectedAssets, setGeospatialSelectedAssets ] = useState({ leftAsset: null, rightAsset: null });
  const [ allAssetsByType, setAllAssetsByType ] = useState({});

  const isLandcover = [ 'landcover', 'natural-vegetation-proportion' ].includes(geospatialSelectedAssets?.geoInfoTypeId);
  const isBiomass = geospatialSelectedAssets?.geoInfoTypeId === 'biomass';
  const isPatches = geospatialSelectedAssets?.geoInfoTypeId === 'patches';
  const isEiiReclass = geospatialSelectedAssets?.geoInfoTypeId === 'eii-reclass';
  const isLandcoverWater = geospatialSelectedAssets?.geoInfoTypeId === 'landcover-water';
  const isConversionAreas = geospatialSelectedAssets?.geoInfoTypeId === 'conversion-areas';
  const selectedGeoInfoTypeId = geospatialSelectedAssets.geoInfoTypeId || geospatialTypesOptions[0]?.value;

  const effectiveMapMaxZoom = highZoomGeoInfoTypes.includes(selectedGeoInfoTypeId)
    ? highMapMaxZoom
    : defaultLeafletMaxZoom;

  const mapOptionsByType = useMemo(
    () => ({ ...mapOptions, maxZoom: effectiveMapMaxZoom }),
    [ effectiveMapMaxZoom ],
  );

  const isYearly = useMemo(() => isYearlyMode(selectedGeoInfoTypeId), [ selectedGeoInfoTypeId ]);

  const leftDate = buildAssetDate(geospatialSelectedAssets?.leftAsset, isYearly);
  const rightDate = buildAssetDate(geospatialSelectedAssets?.rightAsset, isYearly);

  const [ geospatialComp, setGeospatialComp ] = useState({});

  const { isLoading, isSuccess } = fetchStatus;

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const getCurrentTypeAssets = () => {
    const currentGeoInfoTypeId = geospatialSelectedAssets.geoInfoTypeId || geospatialTypesOptions[0]?.value;
    return allAssetsByType[currentGeoInfoTypeId] || [];
  };

  const initializeGeospatialData = () => {
    if (analytic && analytic.assets) {
      const groupedAssets = groupAssetsByType(analytic.assets);
      setAllAssetsByType(groupedAssets);

      const currentGeoInfoTypeId = analytic.geoInfoTypeId;

      // Si es EII Detail, deducir y setear la opción correcta basándose en el geoInfoTypeId
      if (isEiiDetail && onEiiDetailChange && eiiDetailOptions.length > 0) {
        const matchingEiiOption = findEiiDetailOptionByGeoInfoTypeId(currentGeoInfoTypeId, eiiDetailOptions);

        if (matchingEiiOption) {
          onEiiDetailChange(matchingEiiOption);
        }
      }

      setGeospatialSelectedAssets({
        leftAsset: analytic.leftAsset,
        rightAsset: analytic.rightAsset,
        bounds: analytic.bounds,
        geoInfoTypeId: currentGeoInfoTypeId,
        geospatialId: analytic.geospatialId,
      });

      if (analytic.bounds) {
        buildMap({
          bounds: analytic.bounds,
          geoInfoTypeId: currentGeoInfoTypeId,
          leftAsset: analytic.leftAsset,
          rightAsset: analytic.rightAsset,
        });
      }
    }
  };

  const updateGeospatialAssets = async newGeoInfoTypeId => {
    const newTypeAssets = allAssetsByType[newGeoInfoTypeId] || [];

    if (newTypeAssets.length === 0) {
      console.warn(`No hay assets disponibles para el tipo '${newGeoInfoTypeId}'`);
      return false;
    }

    const selectedAssets = selectDefaultAssets(newTypeAssets, newGeoInfoTypeId, geospatialSelectedAssets);

    if (!selectedAssets.leftAsset || !selectedAssets.rightAsset) {
      console.warn(`No se pudieron seleccionar assets válidos para el tipo '${newGeoInfoTypeId}'`);
      return false;
    }

    setGeospatialSelectedAssets(prev => ({
      ...prev,
      ...selectedAssets,
      geoInfoTypeId: newGeoInfoTypeId,
    }));

    await projectApi.updateGeospatialConfig({
      projectId,
      config: {
        leftAssetId: selectedAssets.leftAsset.id,
        rightAssetId: selectedAssets.rightAsset.id,
        geoInfoTypeId: newGeoInfoTypeId,
        geospatialId: geospatialSelectedAssets.geospatialId,
        componentId,
      },
    });

    return true;
  };

  const handleDetailTypeChange = async newChartType => {
    if (onEiiDetailChange) {
      onEiiDetailChange(newChartType);
    }

    if (isEiiDetail) {
      const newGeospatialOptions = GEOSPATIAL_OPTIONS_BY_EII_DETAIL[newChartType.value] || [];
      if (newGeospatialOptions.length > 0) {
        const newGeoInfoTypeId = newGeospatialOptions[0].value;
        await updateGeospatialAssets(newGeoInfoTypeId);
      }
    }
  };

  const handleGeospatialOptionChange = async (event, newValue) => {
    await updateGeospatialAssets(newValue.value);
  };

  useEffect(() => {
    initializeGeospatialData();
  }, [ analytic ]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildMap = async ({ leftAsset, rightAsset, bounds, geoInfoTypeId }) => {
    const hasBounds = Boolean(bounds);
    const geospatialComp = {};
    const hasColorMapLegend = isBiomass || isLandcover || isPatches || isEiiReclass || isLandcoverWater || isConversionAreas;
    if (hasBounds && leftAsset && leftAsset.path && (!isUnique || (isUnique && !rightAsset) || (rightAsset && rightAsset.path))) {

      geospatialComp['display'] = true;
      const ranges = getMinMaxValues(leftAsset, !isUnique ? rightAsset : leftAsset);
      const leftAssetName = formatAssetName(leftAsset, isYearly);
      const rightAssetName = isUnique ? null : formatAssetName(rightAsset, isYearly);

      const rescale = hasColorMapLegend ? undefined : ranges.join(',');
      const geospatialLeftUrl = buildTiTilerUrl({ filePath: leftAsset.path, assetType: geoInfoTypeId, rescale });
      const baseLeafletOptions = getLeafletOptions(bounds);
      const leafletOptions = highZoomGeoInfoTypes.includes(geoInfoTypeId)
        ? { ...baseLeafletOptions, maxZoom: highMapMaxZoom }
        : { ...baseLeafletOptions, maxZoom: defaultLeafletMaxZoom };

      geospatialComp['left'] = {
        indicator: buildLeafletLayerWithHeaders(geospatialLeftUrl, leafletOptions),
        name: `${geospatialTypeOption?.label ?? formatDashedString(geoInfoTypeId)} - ${leftAssetName}`,
        path: leftAsset.path,
      };

      if (!isUnique && rightAsset && rightAsset.path) {
        const geospatialRightUrl = buildTiTilerUrl({ filePath: rightAsset.path, assetType: geoInfoTypeId, rescale });
        geospatialComp['right'] = {
          indicator: buildLeafletLayerWithHeaders(geospatialRightUrl, leafletOptions),
          name: `${geospatialTypeOption?.label ?? formatDashedString(geoInfoTypeId)} - ${rightAssetName}`,
          path: rightAsset.path,
        };
      }

      geospatialComp['colorLegend'] = hasColorMapLegend ?
        getColorMapLegend({ items: analyticPalettes[stringToCamelCase(geoInfoTypeId)], title: legendTitle[geoInfoTypeId] }) :
        buildLegendForIndex({ indexKey: geoInfoTypeId, ranges });

      geospatialComp['bounds'] = bounds;
    }
    setGeospatialComp(geospatialComp);
  };

  useEffect(() => {
    buildMap(geospatialSelectedAssets);
  }, [ `${leftDate}, ${rightDate}`, geospatialSelectedAssets.geoInfoTypeId ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateAssets = async (leftCriteria, rightCriteria) => {
    const currentTypeAssets = getCurrentTypeAssets();
    const { leftAsset, rightAsset } = currentTypeAssets.reduce((acc, a) => {
      if (typeof leftCriteria === 'number' && typeof rightCriteria === 'number') {
        const year = parseInt(a.year, 10);
        if (year === parseInt(leftCriteria, 10)) {
          acc.leftAsset = a;
        }
        if (year === parseInt(rightCriteria, 10)) {
          acc.rightAsset = a;
        }
      } else if (leftCriteria.year && leftCriteria.month && rightCriteria.year && rightCriteria.month) {
        if (a.year === leftCriteria.year && a.month === leftCriteria.month) {
          acc.leftAsset = a;
        }
        if (a.year === rightCriteria.year && a.month === rightCriteria.month) {
          acc.rightAsset = a;
        }
      }
      return acc;
    }, { leftAsset: null, rightAsset: null });

    if (!leftAsset || !rightAsset) {
      console.warn('No se pudieron encontrar los assets para los criterios seleccionados');
      return;
    }

    setGeospatialSelectedAssets(prev => ({ ...prev, leftAsset, rightAsset }));

    await projectApi.updateGeospatialConfig({
      projectId,
      config: {
        leftAssetId: leftAsset.id,
        rightAssetId: rightAsset.id,
        geoInfoTypeId: geospatialSelectedAssets.geoInfoTypeId,
        geospatialId: geospatialSelectedAssets.geospatialId,
        componentId,
      },
    });

  };

  const handleUpdateYearMonth = async yearMonthComparison => {
    await handleUpdateAssets(yearMonthComparison[0], yearMonthComparison[1]);
  };

  const handleUpdateYear = async ([ leftYear, rightYear ]) => {
    await handleUpdateAssets(leftYear, rightYear);
  };

  const handleUpdateSingleYear = async year => {
    const currentTypeAssets = getCurrentTypeAssets();
    const selectedAsset = currentTypeAssets.find(a => a.year === year);

    if (!selectedAsset) {
      console.warn('No se pudo encontrar el asset para el año seleccionado');
      return;
    }

    setGeospatialSelectedAssets(prev => ({
      ...prev,
      leftAsset: selectedAsset,
      rightAsset: null,
    }));

    await projectApi.updateGeospatialConfig({
      projectId,
      config: {
        leftAssetId: selectedAsset.id,
        rightAssetId: null,
        geoInfoTypeId: geospatialSelectedAssets.geoInfoTypeId,
        geospatialId: geospatialSelectedAssets.geospatialId,
        componentId,
      },
    });
  };

  const isInProgressGeoInfo = Boolean(geospatialStats?.inProgress);
  const hasGeoSpatialMap = Boolean(geospatialComp?.left) || Boolean(geospatialComp?.right);
  const hasEvolutionChart = Boolean(geospatialStats?.ndvi);
  const hasLandscapeStats = Boolean(geospatialStats?.landscape);
  const hasLandcoverStats = geospatialStats?.landcover?.datasets?.length > 0;

  const geospatialTypeOption = geospatialTypesOptions.find(option => option.value === geospatialSelectedAssets.geoInfoTypeId)
    || geospatialTypesOptions[0]
    || null;

  const currentDate = (
    isUnique ? geospatialSelectedAssets.leftAsset?.year
    : isYearly ? [ geospatialSelectedAssets.leftAsset?.year, geospatialSelectedAssets.rightAsset?.year ].filter(Boolean)
    : [
      geospatialSelectedAssets.leftAsset ?
        { year: geospatialSelectedAssets.leftAsset.year, month: geospatialSelectedAssets.leftAsset.month }
        : null,
      geospatialSelectedAssets.rightAsset ?
        { year: geospatialSelectedAssets.rightAsset.year, month: geospatialSelectedAssets.rightAsset.month }
        : null,
    ].filter(Boolean)
  );

  return (
    <>
      {(
        hasGeoSpatialMap || hasEvolutionChart || hasLandscapeStats || hasLandcoverStats
        || isLoading || isInProgressGeoInfo) &&
        <Grid item xs={12}>
          <AnalyticCard title={title}>
            <Divider />
            {isLoading && <LoadingBox label={title.toLowerCase()} />}
            {isInProgressGeoInfo && <ProcessBox label="área de interés" />}
            <Grid container spacing={1}>
              {(isSuccess && hasGeoSpatialMap) &&
                <Grid item xs={12}>
                  <Box width="100%" display="flex" mt={2} mb={1} alignItems="center">
                    {isEiiDetail &&
                      <Box my={0.5} display="flex" alignItems="center">
                        <Box display="flex" className={classes.group} p={0.5} ml={1}>
                          {eiiDetailOptions.map((option, index) => (
                            <Fragment key={option.value}>
                              <Chip
                                color={selectedEiiDetail?.value === option.value ? 'secondary.main' : 'gray'}
                                label={option.label}
                                size="small"
                                onClick={() => handleDetailTypeChange(option)}
                                width={120}
                              />
                              {index < eiiDetailOptions.length - 1 && <Box mx={0.5} />}
                            </Fragment>
                          ))}
                        </Box>
                      </Box>
                    }
                    <Box flexGrow={1} />
                    <Box display="flex" flexDirection="column" ml={1}>
                      {/* <InputLabel shrink>Fechas a comparar</InputLabel> */}
                      <Box className={!isUnique ? classes.comparisonYearGroup : undefined} height={32}>
                        {isUnique ? (
                          <AutocompleteWrapper
                            fullWidth
                            value={
                              currentDate
                                ? { value: currentDate, label: currentDate.toString() }
                                : null
                            }
                            options={
                              [ ...new Set(getCurrentTypeAssets().map(a => a.year)) ]
                                .filter(Boolean)
                                .sort((a, b) => b - a)
                                .map(y => ({ value: y, label: y.toString() }))
                            }
                            getOptionSelected={(option, value) => option.value === value?.value}
                            onChange={(e, newValue) => newValue && handleUpdateSingleYear(newValue.value)}
                            size="very-small"
                            classes={{ inputRoot: classes.autocomplete }}
                          />
                        ) : isYearly ? (
                          <FilterYearComparison
                            id="geospatial-year-comparison"
                            prevValue={currentDate}
                            options={
                              [ ...new Set(getCurrentTypeAssets().map(a => a.year)) ].filter(Boolean).sort((a, b) => b - a)
                            }
                            onChange={handleUpdateYear}
                          />
                        ) : (
                          <FilterYearMonthComparison
                            id="geospatial-year-month-comparison"
                            prevValue={currentDate}
                            options={{
                              years: [ ...new Set(getCurrentTypeAssets().map(a => a.year)) ].filter(Boolean).sort((a, b) => b - a),
                              months: [ ...new Set(getCurrentTypeAssets().map(a => a.month)) ].filter(Boolean)
                                .sort((a, b) => a - b),
                            }}
                            onChange={handleUpdateYearMonth}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box ml={1} display="flex" flexDirection="column" width={160}>
                      {/* <InputLabel shrink>Indices</InputLabel> */}
                      <AutocompleteWrapper
                        fullWidth
                        value={geospatialTypeOption}
                        options={geospatialTypesOptions}
                        getOptionSelected={(option, value) => option.value === value?.value}
                        onChange={handleGeospatialOptionChange}
                        size="very-small"
                        classes={{ inputRoot: classes.autocomplete }}
                      />
                    </Box>
                  </Box>
                  <MapWrapper
                    id={`map-${id}`}
                    className={classes.map}
                    options={mapOptionsByType}
                    height={heightVH(interestAreaMapHeight)}
                    layers={
                      [
                        ...interestArea.map(amd => buildLeafletLayer({
                          features: amd.geojson,
                          id: amd.id,
                          name: amd.name,
                        })),
                      ]
                    }
                    fitBoundsOnUpdate
                    comp={geospatialComp}
                    legend={geospatialComp.colorLegend}
                    showPixelValue
                  />
                </Grid>
              }

              {hasEvolutionChart && geospatialSelectedAssets.geoInfoTypeId === 'ndvi' &&
                <Grid item xs={12}>
                  {(hasGeoSpatialMap && !isInProgressGeoInfo) &&
                    <Box mt={2}>
                      <GeoInfo
                        dataToChart={geospatialStats.ndvi}
                        leftDate={leftDate}
                        rightDate={rightDate}
                        geoInfoTypeId={geospatialSelectedAssets.geoInfoTypeId}
                      />
                    </Box>
                  }
                </Grid>
              }

              {[ 'richness', 'gpp', 'albedo', 'ndti', 'ndmi', 'si', 'landcover-water', 'eii-reclass', 'conversion-areas' ]
                .includes(geospatialSelectedAssets.geoInfoTypeId) &&
                <Grid item xs={12}>
                  <GeospatialChart
                    allAssetsByType={allAssetsByType}
                    currentGeoInfoTypeId={geospatialSelectedAssets.geoInfoTypeId}
                  />
                </Grid>
              }
              {hasLandcoverStats && geospatialSelectedAssets.geoInfoTypeId === 'landcover' &&
                <LandcoverStats
                  landcoverAssetSelecteds={geospatialSelectedAssets}
                  landcoverStats={geospatialStats.landcover}
                />
              }
              {(hasGeoSpatialMap && hasLandscapeStats && selectedEiiDetail?.value === 'struct') &&
                <Grid item xs={12}>
                  <Box mt={2}>
                    <GeoSpatialYearComparison
                      data={geospatialStats.landscape}
                      years={[
                        geospatialSelectedAssets.leftAsset?.year,
                        geospatialSelectedAssets.rightAsset?.year,
                      ].filter(Boolean)}
                    />
                  </Box>
                </Grid>
              }
              {geospatialSelectedAssets.geoInfoTypeId === 'natural-vegetation-proportion' &&
                <Grid item xs={12}>
                  <NvpStats
                    allAssetsByType={allAssetsByType}
                    currentGeoInfoTypeId={geospatialSelectedAssets.geoInfoTypeId}
                  />
                </Grid>
              }
            </Grid>
          </AnalyticCard>
        </Grid>
      }
    </>

  );
};

GeospatialAnalytic.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  analytic: PropTypes.object,
  interestArea: PropTypes.array,
  getLeafletOptions: PropTypes.func.isRequired,
  geospatialTypesOptions: PropTypes.array,
  isUnique: PropTypes.bool,
  eiiDetailConfig: PropTypes.shape({
    options: PropTypes.array,
    selected: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
    onChange: PropTypes.func,
  }),
  fetchStatus: PropTypes.shape({
    isLoading: PropTypes.bool,
    isSuccess: PropTypes.bool,
  }),
};


export {
  GeospatialAnalytic,
};
